import _ from "lodash";
import { BookingFiltersState, FilterConfigItem, FilterOption } from "../hooks/useListingFilters";

export const setCookie = (cname, cvalue, exdays) => {
  const d = new Date();
  d.setTime(d.getTime() + exdays * 24 * 60 * 60 * 1000);
  const expires = "expires=" + d.toUTCString();
  document.cookie = cname + "=" + cvalue + ";" + expires + ";path=/";
};

export const getCookie = (cname) => {
  const name = cname + "=";
  const ca = document.cookie.split(";");
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) == " ") {
      c = c.substring(1);
    }
    if (c.indexOf(name) == 0) {
      return c.substring(name.length, c.length);
    }
  }
  return "";
};

export const checkCookie = (cname) => {
  const user = getCookie(cname);
  if (user != "") {
    return true;
  } else {
    return false;
  }
};

export const validateEmail = (email: string) => {
  const expression = /^[\w-\.]+@([\w-]+\.)+[\w-]{2,3}$/;
  return expression.test(email?.toLowerCase());
};

export const checkScreenAccess = (key: string, isDropDown: boolean, userScreenAccess: string[]): boolean => {
  if (userScreenAccess.length === 0) {
    return true;
  }

  if (!isDropDown && userScreenAccess.length > 0) {
    return userScreenAccess.includes(key);
  }

  if (isDropDown && userScreenAccess.length > 0) {
    if (key === "contracts") {
      return _.some(["bookings", "leases", "renewalsInProgress", "upcomingRenewals", "payments"], (elem) =>
        _.includes(userScreenAccess, elem),
      );
    } else if (key === "contacts") {
      return _.some(["tenants", "owners"], (elem) => _.includes(userScreenAccess, elem));
    } else {
      return _.some(["serviceProviders", "tickets"], (elem) => _.includes(userScreenAccess, elem));
    }
  }

  return false;
};

export const bookingStatuses = [
  {
    label: "All",
    value: "all",
  },
  {
    label: "Draft",
    value: "draft",
  },
  {
    label: "Draft-Submitted",
    value: "booking_created",
  },
  {
    label: "Tenant Invitation Sent",
    value: "tenant_invitation_sent",
  },
  {
    label: "Documents Approved",
    value: "documents_approved",
  },
  {
    label: "Kyc Approved",
    value: "kyc_approved",
  },
  {
    label: "Cancelled Bookings",
    value: "booking_cancelled",
  },
  {
    label: "Sent For Booking Approval",
    value: "sent_for_booking_approval",
  },
  {
    label: "Booking Form Sent",
    value: "booking_form_sent",
  },
  {
    label: "Awaiting Deposit",
    value: "awaiting_deposit",
  },
  {
    label: "Deposit Paid",
    value: "deposit_paid",
  },

  {
    label: "Rent Payments Received",
    value: "rent_payments_received",
  },
  {
    label: "Sent For Lease Approval",
    value: "sent_for_lease_approval",
  },
  {
    label: "Contract Created",
    value: "contract_created",
  },
  {
    label: "Awaiting Tenant Signature",
    value: "awaiting_tenant_signature",
  },
  {
    label: "Awaiting Landlord Signature",
    value: "awaiting_landlord_signature",
  },
  {
    label: "Contract Signed",
    value: "contract_signed",
  },
];

export const usagesTypeFilters = (usagesTypeInitial) => {
  return [
    {
      name: "usagesType",
      label: "Usage Type",
      initialValue: usagesTypeInitial,
      options: [
        {
          value: "all",
          label: "All",
          title: "usagesType",
        },
        {
          value: "residential",
          label: "Residential",
          title: "usagesType",
        },
        {
          value: "commercial",
          label: "Commercial",
          title: "usagesType",
        },
      ],
    },
  ];
};

export const getLeaseTypesForTenant = (data) => {
  let leaseType = [];
  if (data?.leaseDetail?.length > 0) {
    data?.leaseDetail?.forEach((element) => {
      leaseType?.push(element.propertyDetails?.usagesType?.toLowerCase());
    });

    if (leaseType?.includes("residential")) {
      return data?.residentialKycForm;
    } else if (leaseType?.includes("commercial")) {
      return data?.commercialKycForm;
    } else if (leaseType?.includes("residential") && leaseType?.includes("commercial")) {
      // Merge and deduplicate based on `name`
      let mergedResult = [...data?.residentialKycForm, ...data?.commercialKycForm];

      const uniqueKycForm = Object.values(
        mergedResult.reduce((acc, item) => {
          if (!acc[item.name]) {
            acc[item.name] = item; // only set if not already added
          }
          return acc;
        }, {}),
      );

      return uniqueKycForm;
    } else {
      return data?.residentialKycForm;
    }
  } else {
    return data?.residentialKycForm;
  }
};

// for filter kyc detail for edit tenant screen

export const kycDataFields = (data) => {
  const filterKyc = Object.fromEntries(
    Object.entries(data)
      .filter(([key]) => key.startsWith("kyc-"))
      .map(([key, value]) => {
        const cleanKey = key.replace("kyc-", ""); // remove 'kyc-' prefix
        if (value && typeof value === "object" && !Array.isArray(value)) {
          return [cleanKey, value["value"]];
        }
        return [cleanKey, value];
      }),
  );

  return filterKyc;
};

export const calCulateVatInPercent = (amount: string | number = 0, vatPercent: string | number = 0) => {
  // Trim if input is string, then convert to number
  const baseAmount = typeof amount === "string" ? Number(amount.trim()) || 0 : Number(amount) || 0;
  const vatRate = typeof vatPercent === "string" ? Number(vatPercent.trim()) || 0 : Number(vatPercent) || 0;
  // Calculate VAT only if both exist
  const vat = baseAmount && vatRate ? (baseAmount * vatRate) / 100 : 0;

  // Add VAT to amount
  const actualAmount = baseAmount + vat;
  return {
    amount: baseAmount,
    vat,
    actualAmount,
    vatRate,
  };
};

export const imageFormats = [
  "jpg",
  "jpeg",
  "png",
  "gif",
  "webp",
  "avif",
  "svg",
  "apng",
  "ico",
  "bmp",
  "heic", // limited (Safari/iOS only)
  "heif", // limited (Safari/iOS only)
  "jp2", // JPEG 2000 (Safari only)
  "j2k", // JPEG 2000 (Safari only)
  "jxr", // legacy Edge only
  "wdp", // legacy Edge only
  "hdp", // legacy Edge only
];

type Props = {
  usagesType: FilterOption | null;
  property: FilterOption | null;
  unit: FilterOption | null;
  propertyOptions: FilterOption[];
  unitOptions: FilterOption[];
};

export const listFilterConfig = ({
  usagesType,
  property,
  unit,
  propertyOptions,
  unitOptions,
}: Props): FilterConfigItem[] => [
  {
    name: "usagesType",
    label: "Usage Type",
    initialValue: usagesType,
    options: [
      { value: "all", label: "All", title: "usagesType" },
      { value: "residential", label: "Residential", title: "usagesType" },
      { value: "commercial", label: "Commercial", title: "usagesType" },
    ],
  },
  {
    name: "property",
    label: "Property",
    initialValue: property,
    options: [
      {
        value: "",
        label: "All Properties",
        title: "property",
      },
      ...propertyOptions,
    ],
    search: true,
  },
  {
    name: "unit",
    label: "Unit",
    initialValue: unit,
    options: [
      {
        value: "",
        label: "All Units",
        title: "unit",
      },
      ...unitOptions,
    ],
    search: true,
    disabled: !property?.value,
  },
];

/** True when the property filter represents "all properties" (including initial `all`). */
export function isAllPropertiesSelection(value: unknown): boolean {
  return value === undefined || value === null || value === "" || value === "all";
}

type ListFilterWithPortfolioUnitsArgs = {
  state: BookingFiltersState;
  propertyOptions: FilterOption[];
  portfolioUnits: FilterOption[];
  bookingUnits: FilterOption[];
  /** Shown on the unit Select while portfolio-wide units are loading */
  portfolioUnitsLoading?: boolean;
};

/** Uses portfolio `unitsDropdown` when all properties; otherwise per-property booking units. */
export function listFilterConfigWithPortfolioUnits({
  state,
  propertyOptions,
  portfolioUnits,
  bookingUnits,
  portfolioUnitsLoading = false,
}: ListFilterWithPortfolioUnitsArgs): FilterConfigItem[] {
  const allProps = isAllPropertiesSelection(state.property?.value);
  const unitOptions = allProps ? portfolioUnits : portfolioUnits;
  const rows = listFilterConfig({
    usagesType: state.usagesType,
    property: state.property,
    unit: state.unit,
    propertyOptions,
    unitOptions,
  });
  return rows.map((item) =>
    item.name === "unit"
      ? {
          ...item,
          disabled: false,
          unitLoading: allProps ? portfolioUnitsLoading : false,
        }
      : item,
  );
}
